/**
 * UnauthorizedPage - Shown when user lacks permission to access a route
 *
 * Simple, clean page with:
 * - Lock icon
 * - "Unauthorized" title
 * - Helpful message
 * - "Go Back" button
 */

import {ShieldX} from 'lucide-react'
import React, {useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import styled from 'styled-components'
import {Button} from '~/src/design-system/Primitives'

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: var(--space-6);
  background: #fafafa;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #fff3f3;
  color: #e53935;
  margin-bottom: var(--space-6);
`

const Title = styled.h1`
  font-family: 'Inter', sans-serif;
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 var(--space-3) 0;
  text-align: center;
`

const Message = styled.p`
  font-family: 'Open Sans', sans-serif;
  font-size: 1rem;
  color: var(--neutral-600);
  margin: 0 0 var(--space-8) 0;
  text-align: center;
  max-width: 400px;
  line-height: 1.5;
`

// ============================================================================
// COMPONENT
// ============================================================================

export const UnauthorizedPage: React.FC = () => {
  const navigate = useNavigate()

  const handleGoBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleGoHome = useCallback(() => {
    navigate('/dashboard')
  }, [navigate])

  return (
    <Container>
      <IconWrapper>
        <ShieldX size={40} />
      </IconWrapper>
      <Title>Unauthorized</Title>
      <Message>
        You don&apos;t have permission to access this page. Please contact your administrator if you
        believe this is an error.
      </Message>
      <Button variant="secondary" onClick={handleGoBack}>
        Go Back
      </Button>
      <Button variant="tertiary" onClick={handleGoHome}>
        Return to Dashboard
      </Button>
    </Container>
  )
}

export default UnauthorizedPage
